import api from "./api";

/**
 * Issue #271: bounded retry for requests whose failure the user would
 * otherwise read as data loss.
 *
 * The defect this exists for: `api.get(...).catch(() => {})` left `summary`
 * null and the Dashboard rendered `summary?.total_runs ?? 0` — a permanent,
 * silent "Total Runs 0" after a player had just logged a match. `0` is the
 * worst possible failure value precisely because it is a *legitimate* one:
 * indistinguishable from success, so nothing throws and no gate catches it.
 *
 * Retry is the first line of defence, not the only one. It buys back the
 * overwhelmingly common case — one transient 503/reset from a saturated
 * worker on a phone at a cricket ground, which is the designed use case —
 * without ever putting a wrong number on screen. When the retries are
 * exhausted the caller must still render an unmistakable non-value (a dash,
 * a skeleton, an error state), never a plausible real one. This module
 * deliberately does not retry forever: an unbounded retry against a truly
 * down server is a hidden request loop, and the user is owed the truth
 * within a couple of seconds, not a spinner that never resolves.
 *
 * Only for IDEMPOTENT requests. Every GET here is a read; the one POST that
 * uses `postWithRetry` (`/players/me/coach-feedback-seen`) sets a watermark
 * to "now" and is idempotent by construction — re-sending it cannot create
 * a duplicate or double-apply anything.
 */

// Three attempts total (the original + two retries). Enough to ride out a
// single blip and the immediate re-blip behind it; short enough that a real
// outage surfaces as an error state in well under two seconds of waiting.
const DEFAULT_ATTEMPTS = 3;
// Backoff before attempt 2 and attempt 3. Short and non-uniform so two
// components mounting together don't re-collide in lockstep on the same
// saturated server.
const BACKOFF_MS = [400, 1200];

/**
 * Retry only what a retry can plausibly fix.
 *
 * - No `response` at all — network drop, DNS, connection reset, timeout.
 *   This is the phone-at-the-ground case, and the `read ECONNRESET` seen in
 *   #265's captured logs.
 * - 5xx — the server failed, not the request. #271's evidence is a literal
 *   `POST /api/players/me/coach-feedback-seen 503`.
 * - 429 — explicitly "try again".
 *
 * Everything else (401/403/404/400/409/422) is a *correct, stable* answer
 * about this caller or this payload. Retrying it would hammer the API to
 * arrive at the same reply, and would slow down real UI paths that treat a
 * 404 as meaningful — Dashboard.jsx deliberately reads 404 as "no profile
 * yet", not as an error.
 */
export function isRetryable(error) {
  const status = error?.response?.status;
  if (status == null) return true;
  if (status === 429) return true;
  return status >= 500;
}

/**
 * "We never got an answer", as opposed to "the server answered, and the
 * answer was no."
 *
 * Deliberately the same predicate as `isRetryable` — exactly the failures
 * worth re-sending are the ones where the application never actually
 * spoke — but named separately because callers use it for a different
 * decision: what to RENDER, not whether to re-send.
 *
 * The distinction matters. A 400 from `/goals` for a coach caller (no
 * `player_id`, see `resolveScope`) is a correct, stable, repeatable answer
 * about who is asking. Showing "Couldn't load — try again" for it offers a
 * button that can never work and blames the network for a routing
 * decision. Only a genuine unknown earns the failure state.
 */
export const isUnknownAnswer = isRetryable;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry(send, attempts) {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await send();
    } catch (error) {
      lastError = error;
      const isLast = attempt === attempts - 1;
      if (isLast || !isRetryable(error)) throw error;
      await sleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]);
    }
  }
  throw lastError;
}

/** Idempotent GET with bounded retry. Rejects (does not swallow) when every
 * attempt fails — the caller owns what the user sees, which is the whole
 * point of #271. */
export function getWithRetry(path, { attempts = DEFAULT_ATTEMPTS, headers } = {}) {
  return withRetry(() => (headers ? api.get(path, { headers }) : api.get(path)), attempts);
}

/** Bounded retry for a POST the caller has asserted is idempotent. */
export function postWithRetry(path, body, { attempts = DEFAULT_ATTEMPTS } = {}) {
  return withRetry(() => api.post(path, body), attempts);
}
